import React, {useEffect, useRef, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {PurchaseService} from "../../service/PurchaseService";
import {PurchaseOption} from "../../model/user/purchase/PurchaseOption";
import {getSessionId} from "../../util/TrackingUtil";
import {trackInitiateCheckout} from "../../util/FacebookPixelUtil";

type Plan = "full" | "installment";

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function EasEnrollModal({open, onClose}: Props): JSX.Element {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [plan, setPlan] = useState<Plan>("full");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const emailRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setError(null);
            setTimeout(() => emailRef.current?.focus(), 60);
        }
    }, [open]);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape" && open) onClose();
        }

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError(null);

        const purchaseOption = plan === "installment"
            ? PurchaseOption.COURSE_EAS_SYSTEM_INSTALLMENT
            : PurchaseOption.COURSE_EAS_SYSTEM;

        try {
            const checkoutEventId = await trackInitiateCheckout(
                email,
                undefined,
                purchaseOption.price,
                "USD",
                [purchaseOption.toString()],
                purchaseOption.description,
            ).catch(() => "");

            const response = await PurchaseService.initStripeCheckout({
                purchaseOptionName: purchaseOption.toString(),
                name: name.trim(),
                email: email.trim().toLowerCase(),
                source: localStorage.getItem("firstJoinedFrom") ?? "eas-system",
                successRedirectPath: "/system/thank-you",
                errorRedirectPath: "/system?status=cancelled",
                eventUrl: window.location.href,
                sessionId: getSessionId(),
                eventId: checkoutEventId || undefined,
            });

            if (response?.checkoutUrl) {
                window.location.href = response.checkoutUrl;
                return;
            }
            throw new Error("No checkout URL returned. Try again or email support@effectiveactionsystem.com.");
        } catch (err: any) {
            setError(err?.message ?? "Something went wrong. Please try again.");
            setSubmitting(false);
        }
    }

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    onClick={onClose}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
                        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "1rem",
                    }}>
                    <motion.div
                        initial={{opacity: 0, y: 24, scale: 0.96}}
                        animate={{opacity: 1, y: 0, scale: 1}}
                        exit={{opacity: 0, y: 24, scale: 0.96}}
                        transition={{duration: 0.25, ease: "easeOut"}}
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#0f1a14", border: "1px solid rgba(42,209,103,0.25)",
                            borderRadius: 20, padding: "2rem", maxWidth: 480, width: "100%",
                            color: "#fff", boxShadow: "0 32px 64px -24px rgba(0,0,0,0.65)",
                        }}>
                        <h2 style={{margin: "0 0 0.5rem", fontSize: "1.5rem", fontWeight: 600}}>
                            Enroll your teenager
                        </h2>
                        <p style={{margin: "0 0 1.5rem", color: "rgba(255,255,255,0.7)", fontSize: "0.95rem"}}>
                            We'll send onboarding instructions to your email after Stripe confirms payment.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <label style={{
                                display: "block",
                                fontSize: "0.85rem",
                                marginBottom: 6,
                                color: "rgba(255,255,255,0.8)"
                            }}>
                                Your name
                            </label>
                            <input
                                type="text" value={name} onChange={e => setName(e.target.value)}
                                required minLength={2} placeholder="e.g. Sarah"
                                style={inputStyle}/>

                            <label style={{
                                display: "block",
                                fontSize: "0.85rem",
                                marginTop: "1rem",
                                marginBottom: 6,
                                color: "rgba(255,255,255,0.8)"
                            }}>
                                Your email
                            </label>
                            <input
                                ref={emailRef}
                                type="email" value={email} onChange={e => setEmail(e.target.value)}
                                required placeholder="you@example.com"
                                style={inputStyle}/>

                            <fieldset style={{border: "none", padding: 0, margin: "1.25rem 0 0"}}>
                                <legend style={{
                                    fontSize: "0.85rem",
                                    marginBottom: 8,
                                    color: "rgba(255,255,255,0.8)",
                                    padding: 0
                                }}>
                                    Payment plan
                                </legend>
                                <PlanRadio
                                    label="Pay in full — $2,997"
                                    sub="Best value · single payment"
                                    selected={plan === "full"}
                                    onSelect={() => setPlan("full")}/>
                                <PlanRadio
                                    label="3 monthly payments — $1,097"
                                    sub="Start right now and pay the rest later"
                                    selected={plan === "installment"}
                                    onSelect={() => setPlan("installment")}/>
                            </fieldset>

                            {error && (
                                <p style={{color: "#ff7777", fontSize: "0.85rem", marginTop: "1rem"}}>
                                    {error}
                                </p>
                            )}

                            <div style={{display: "flex", gap: 12, marginTop: "1.5rem"}}>
                                <button type="button" onClick={onClose} disabled={submitting} style={cancelBtnStyle}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={submitting} style={submitBtnStyle}>
                                    {submitting ? "Redirecting…" : "Continue to checkout →"}
                                </button>
                            </div>

                            <p style={{
                                fontSize: "0.78rem",
                                color: "rgba(255,255,255,0.5)",
                                marginTop: "1.25rem",
                                lineHeight: 1.5,
                                textAlign: "center",
                            }}>
                                By continuing you agree to our{" "}
                                <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                                   style={{color: "#2ad167"}}>privacy policy</a>,{" "}
                                <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
                                   style={{color: "#2ad167"}}>terms of service</a>{" "}
                                and the{" "}
                                <a href="/legal/eas-offer-terms" target="_blank" rel="noopener noreferrer"
                                   style={{color: "#2ad167"}}>EAS Special Offer Terms</a>.
                            </p>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function PlanRadio({label, sub, selected, onSelect}: {
    label: string;
    sub: string;
    selected: boolean;
    onSelect: () => void
}) {
    return (
        <button
            type="button" onClick={onSelect}
            style={{
                display: "block", width: "100%", textAlign: "left", marginBottom: 8,
                background: selected ? "rgba(42,209,103,0.12)" : "transparent",
                border: selected ? "2px solid #2ad167" : "2px solid rgba(255,255,255,0.15)",
                color: "#fff", borderRadius: 12, padding: "0.85rem 1rem", cursor: "pointer",
                transition: "background 0.15s, border-color 0.15s",
            }}>
            <div style={{display: "flex", alignItems: "center", gap: 12}}>
                <span style={{
                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                    border: selected ? "2px solid #2ad167" : "2px solid rgba(255,255,255,0.4)",
                    background: selected ? "#2ad167" : "transparent",
                }}/>
                <div>
                    <div style={{fontWeight: 600, fontSize: "0.95rem"}}>{label}</div>
                    <div style={{color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", marginTop: 2}}>{sub}</div>
                </div>
            </div>
        </button>
    );
}

const inputStyle: React.CSSProperties = {
    width: "100%", padding: "0.75rem 1rem", borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.04)",
    color: "#fff", fontSize: "1rem", boxSizing: "border-box",
};

const cancelBtnStyle: React.CSSProperties = {
    flex: "0 0 auto", padding: "0.75rem 1.25rem", borderRadius: 10,
    background: "transparent", color: "rgba(255,255,255,0.75)",
    border: "1px solid rgba(255,255,255,0.18)", cursor: "pointer", fontWeight: 500,
};

const submitBtnStyle: React.CSSProperties = {
    flex: "1 1 auto", padding: "0.75rem 1.25rem", borderRadius: 10,
    background: "#2ad167", color: "#0b1410", border: "none",
    cursor: "pointer", fontWeight: 700, fontSize: "0.95rem",
};
